import { z } from 'zod';
import { APPOINTMENT_APPROVALS, APPOINTMENT_CANCELLERS, APPOINTMENT_HOST_KINDS, APPOINTMENT_STATUSES, HOST_EXCEPTION_KINDS, PAYMENT_STATUSES, SESSION_ONLINE_PAYMENT_MODES, STAFF_ROLES } from '@bulk/types';
import { dateOnlySchema, dateTimeSchema, idSchema, patchOf, publicAssetSchema, timeHHMM, weekdaySchema } from './common';

export const MAX_SLOT_RANGE_DAYS = 31;
export const MAX_EXCEPTION_RANGE_DAYS = 62;
export const HOST_EXCEPTION_NOTE_MAX_LENGTH = 200;
export const MAX_REPORT_RANGE_DAYS = 366;
export const DECLINE_REASON_MIN_LENGTH = 10;
export const DECLINE_REASON_MAX_LENGTH = 500;

export const createAppointmentTypeSchema = z.object({
  name: z.string().trim().min(2).max(120),
  nameLocalized: z.record(z.string(), z.string().trim().max(120)).default({}),
  description: z.string().trim().max(2000).nullable().default(null),
  durationMinutes: z.number().int().min(5).max(480),
  hostKind: z.enum(APPOINTMENT_HOST_KINDS).default('TRAINER'),
  hostRole: z.enum(STAFF_ROLES).nullable().default(null).describe('For a STAFF type, the role whose people host it (a tour given by reception); ignored for a TRAINER type'),
  usesPackage: z.boolean().default(false).describe('Each booking takes one session from a package plan the member bought for this type'),
  priceMinor: z.number().int().nonnegative().default(0).describe('What one session costs, paid at the gym, for a type that uses no package; 0 is free'),
  memberBookable: z.boolean().default(true).describe('Members may book it themselves; otherwise only staff book it'),
  requiresApproval: z.boolean().nullable().default(null).describe("Whether a member's booking waits for the host's yes; null follows the gym's setting"),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#5685d4'),
  sortOrder: z.number().int().default(0),
  trainerIds: z.array(idSchema).max(100).default([]).describe('For a TRAINER type, the trainers who give it; empty means every trainer. Ignored for a STAFF type'),
});
export type CreateAppointmentTypeInput = z.infer<typeof createAppointmentTypeSchema>;

export const updateAppointmentTypeSchema = patchOf(createAppointmentTypeSchema).extend({
  active: z.boolean().optional(),
});
export type UpdateAppointmentTypeInput = z.infer<typeof updateAppointmentTypeSchema>;

export const appointmentTypeSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  description: z.string().nullable(),
  durationMinutes: z.number().int(),
  hostKind: z.enum(APPOINTMENT_HOST_KINDS),
  hostRole: z.enum(STAFF_ROLES).nullable(),
  usesPackage: z.boolean(),
  priceMinor: z.number().int(),
  currency: z.string(),
  memberBookable: z.boolean(),
  requiresApproval: z.boolean().nullable(),
  needsApproval: z.boolean().describe("Whether a member's booking of this type waits for the host's yes today: the type's own choice, else the gym's"),
  color: z.string(),
  active: z.boolean(),
  sortOrder: z.number().int(),
  trainerIds: z.array(idSchema).describe('The trainers who give it; empty means every trainer'),
  createdAt: z.string(),
});
export type AppointmentTypeDto = z.infer<typeof appointmentTypeSchema>;

export const appointmentTypeListQuerySchema = z.object({
  includeInactive: z.stringbool().optional(),
});

/** One host: a trainer from the roster, or a member of staff by their login. Exactly one is set. */
export const hostRefSchema = z.object({
  trainerId: idSchema.optional(),
  staffUserId: idSchema.optional(),
});
export type HostRef = z.infer<typeof hostRefSchema>;

export const availabilityWindowSchema = z.object({
  locationId: idSchema,
  weekday: weekdaySchema,
  startTime: timeHHMM,
  endTime: timeHHMM,
});
export type AvailabilityWindowInput = z.infer<typeof availabilityWindowSchema>;

export const setAvailabilitySchema = hostRefSchema.extend({
  windows: z.array(availabilityWindowSchema).max(100).describe("Every window the host offers; replaces what was there. Leave the host out to set the caller's own hours"),
});
export type SetAvailabilityInput = z.infer<typeof setAvailabilitySchema>;

export const availabilitySchema = z.object({
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  hostName: z.string(),
  windows: z.array(availabilityWindowSchema.extend({ id: idSchema, locationName: z.string() })),
});
export type AvailabilityDto = z.infer<typeof availabilitySchema>;

export const hostExceptionQuerySchema = hostRefSchema.extend({
  from: dateOnlySchema.describe("The first day, in the gym's time zone"),
  to: dateOnlySchema.describe(`The last day, included; at most ${MAX_EXCEPTION_RANGE_DAYS} days after from`),
});
export type HostExceptionQuery = z.infer<typeof hostExceptionQuerySchema>;

export const addHostExceptionSchema = hostRefSchema
  .extend({
    kind: z.enum(HOST_EXCEPTION_KINDS).describe('BLOCK takes the day or some hours off; EXTRA adds hours at one branch'),
    date: dateOnlySchema.describe("The date, in the gym's time zone; today or later"),
    startTime: timeHHMM.nullable().default(null).describe('Leave both times out to block the whole day'),
    endTime: timeHHMM.nullable().default(null),
    locationId: idSchema.nullable().default(null).describe('The branch of the extra hours; a block names none, it covers every branch'),
    note: z.string().trim().max(HOST_EXCEPTION_NOTE_MAX_LENGTH).nullable().default(null),
  })
  .refine((v) => (v.startTime === null) === (v.endTime === null), { path: ['endTime'], message: 'Give both times, or neither for the whole day' })
  .refine((v) => v.kind === 'BLOCK' || (v.startTime !== null && v.locationId !== null), { path: ['locationId'], message: 'Extra hours need a branch and times' })
  .refine((v) => v.kind === 'EXTRA' || v.locationId === null, { path: ['locationId'], message: 'A block covers every branch' });
export type AddHostExceptionInput = z.infer<typeof addHostExceptionSchema>;

export const hostExceptionSchema = z.object({
  id: idSchema,
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  kind: z.enum(HOST_EXCEPTION_KINDS),
  date: dateOnlySchema,
  startTime: z.string().nullable().describe('Null on a block of the whole day'),
  endTime: z.string().nullable(),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type HostExceptionDto = z.infer<typeof hostExceptionSchema>;

export const hostCalendarSchema = z.object({
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  hostName: z.string(),
  exceptions: z.array(hostExceptionSchema),
  leaveDays: z.array(dateOnlySchema).describe('Days approved leave keeps the host away'),
  holidays: z.array(z.object({ date: dateOnlySchema, kind: z.enum(['PUBLIC_HOLIDAY', 'GYM_HOLIDAY']) })).describe('Days the weekly hours do not run; extra hours still do'),
});
export type HostCalendarDto = z.infer<typeof hostCalendarSchema>;

export const hostExceptionConflictSchema = z.object({
  id: idSchema,
  startsAt: z.string(),
  endsAt: z.string(),
  memberName: z.string(),
  typeName: z.string(),
  typeNameLocalized: z.record(z.string(), z.string()),
  pending: z.boolean().describe("A member's request still waiting for an answer"),
});
export type HostExceptionConflictDto = z.infer<typeof hostExceptionConflictSchema>;

export const addHostExceptionResultSchema = z.object({
  exception: hostExceptionSchema,
  details: z.object({
    conflicts: z.array(hostExceptionConflictSchema).describe('Sessions already booked in the blocked time: they stay booked; cancel or move them'),
  }),
});
export type AddHostExceptionResultDto = z.infer<typeof addHostExceptionResultSchema>;

export const appointmentHostSchema = z.object({
  kind: z.enum(APPOINTMENT_HOST_KINDS),
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()).describe("A trainer's name in each language the roster spells it in; name stands in for the rest"),
  photo: publicAssetSchema.nullable(),
  locationIds: z.array(idSchema).describe('The branches where the host has weekly hours or extra hours to come'),
});
export type AppointmentHostDto = z.infer<typeof appointmentHostSchema>;

export const appointmentHostQuerySchema = z.object({
  typeId: idSchema.optional().describe('Only hosts who can give this type; every host with weekly hours otherwise'),
  all: z.stringbool().optional().describe('Everyone who could host, weekly hours or not (appointments.manage): to set hours for someone new'),
});

export const slotQuerySchema = hostRefSchema.extend({
  typeId: idSchema,
  from: dateOnlySchema.describe("The first day, in the gym's time zone"),
  to: dateOnlySchema.describe(`The last day, included; at most ${MAX_SLOT_RANGE_DAYS} days after from`),
  locationId: idSchema.optional(),
});
export type SlotQuery = z.infer<typeof slotQuerySchema>;

export const appointmentSlotSchema = z.object({
  startsAt: z.string(),
  endsAt: z.string(),
  locationId: idSchema,
  locationName: z.string(),
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  hostName: z.string(),
  hostNameLocalized: z.record(z.string(), z.string()),
});
export type AppointmentSlotDto = z.infer<typeof appointmentSlotSchema>;

export const createAppointmentSchema = hostRefSchema.extend({
  typeId: idSchema,
  memberId: idSchema.optional().describe('Staff book for a member; a member leaves it out and books for themselves'),
  leadId: idSchema.optional().describe('Staff book a tour or consultation for a lead instead of a member: a type a member of staff gives without a package'),
  locationId: idSchema,
  startsAt: dateTimeSchema,
  roomId: idSchema.nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  payOnline: z.boolean().optional().describe("A member booking a priced session: pay online now. With the gym's switch on OPTIONAL it is the member's choice; REQUIRED pays online unless this is false, which is refused (422 ONLINE_PAYMENT_REQUIRED); OFF refuses true (409 ONLINE_PAYMENT_UNAVAILABLE)"),
  returnUrl: z.url().optional().describe('Where the bank sends the member back after paying online'),
  locale: z.enum(['en', 'ka']).optional(),
});
export type CreateAppointmentInput = z.infer<typeof createAppointmentSchema>;

export const cancelAppointmentSchema = z.object({
  reason: z.string().trim().max(300).optional(),
  returnSession: z.boolean().optional().describe("Staff or the host give the package session back although the gym's cancellation window has passed"),
});
export type CancelAppointmentInput = z.infer<typeof cancelAppointmentSchema>;

export const decideAppointmentSchema = z.object({
  decision: z.enum(['APPROVE', 'DECLINE']),
  reason: z.string().max(2000).optional().describe(`Required to decline: ${DECLINE_REASON_MIN_LENGTH} to ${DECLINE_REASON_MAX_LENGTH} characters the member reads`),
});
export type DecideAppointmentInput = z.infer<typeof decideAppointmentSchema>;

export const appointmentListQuerySchema = hostRefSchema.extend({
  from: dateTimeSchema,
  to: dateTimeSchema,
  locationId: idSchema.optional(),
  memberId: idSchema.optional(),
  leadId: idSchema.optional(),
  status: z.enum(APPOINTMENT_STATUSES).optional(),
});
export type AppointmentListQuery = z.infer<typeof appointmentListQuerySchema>;

export const appointmentSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  typeId: idSchema,
  typeName: z.string(),
  typeNameLocalized: z.record(z.string(), z.string()),
  typeColor: z.string(),
  memberId: idSchema.nullable(),
  leadId: idSchema.nullable().describe('Set instead of memberId for a tour or consultation booked for a lead'),
  memberName: z.string().describe("The member's name, or the lead's"),
  memberNumber: z.string().nullable(),
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  hostName: z.string(),
  hostNameLocalized: z.record(z.string(), z.string()).describe("A trainer host's name in each language the roster spells it in"),
  locationId: idSchema,
  locationName: z.string(),
  roomId: idSchema.nullable(),
  roomName: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: z.enum(APPOINTMENT_STATUSES),
  packageId: idSchema.nullable(),
  sessionReturned: z.boolean().nullable().describe('For a cancelled booking that took a package session: whether the session went back; null otherwise'),
  priceMinor: z.number().int().describe("The type's price for one session; 0 when it uses a package or is free"),
  currency: z.string(),
  note: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelledBy: z.enum(APPOINTMENT_CANCELLERS).nullable(),
  cancellationReason: z.string().nullable(),
  approval: z.enum(APPOINTMENT_APPROVALS).nullable().describe("The host's answer to a member's request; null when the type needed none"),
  decidedAt: z.string().nullable(),
  declineReason: z.string().nullable(),
  markedAt: z.string().nullable(),
  onlinePayment: z
    .object({
      paymentId: idSchema,
      status: z.enum(PAYMENT_STATUSES),
      refundedMinor: z.number().int(),
      heldUntil: z.string().nullable().describe('While the time is held for the payment: when it is given up if the payment has not gone through'),
      payUrl: z.string().nullable().describe("The bank's checkout, while the time is held and the payment is waiting"),
    })
    .nullable()
    .describe('A session the member pays online; null when it is paid at the gym or from a package'),
  createdAt: z.string(),
});
export type AppointmentDto = z.infer<typeof appointmentSchema>;

export const appointmentRequestSchema = appointmentSchema.extend({
  respondBy: z.string().describe("When the request lapses unanswered: the gym's time to answer after it was made, or its cutoff before the session, whichever comes first"),
});
export type AppointmentRequestDto = z.infer<typeof appointmentRequestSchema>;

export const sessionPackageSchema = z.object({
  id: idSchema,
  memberId: idSchema,
  typeId: idSchema,
  typeName: z.string(),
  typeNameLocalized: z.record(z.string(), z.string()),
  planId: idSchema.nullable(),
  planName: z.string().nullable(),
  orderId: idSchema.nullable(),
  sessionsTotal: z.number().int(),
  balance: z.number().int().describe('Sessions not yet booked'),
  startsAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  usable: z.boolean().describe('Sessions left, not expired and not taken back by a refund'),
  createdAt: z.string(),
});
export type SessionPackageDto = z.infer<typeof sessionPackageSchema>;

export const sessionPackageQuerySchema = z.object({
  memberId: idSchema,
});

export const myAppointmentsSchema = z.object({
  upcoming: z.array(appointmentSchema),
  recent: z.array(appointmentSchema),
  packages: z.array(sessionPackageSchema),
  cancellationWindowHours: z.number().int().describe('Cancelling a package session later than this before it starts keeps the session used'),
  requestTtlHours: z.number().int().describe('A request the host has not answered expires this long after it was made'),
  onlinePayment: z.enum(SESSION_ONLINE_PAYMENT_MODES).describe("How the gym takes payment for a priced session that is not from a package and needs no host's yes; OFF while it has no working payment provider"),
});
export type MyAppointmentsDto = z.infer<typeof myAppointmentsSchema>;

export const myHostSchema = z.object({
  trainerId: idSchema.nullable(),
  staffUserId: idSchema.nullable(),
  name: z.string(),
});
export type MyHostDto = z.infer<typeof myHostSchema>;

export const appointmentReportQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
});
export type AppointmentReportQuery = z.infer<typeof appointmentReportQuerySchema>;

export const appointmentReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: z.string(),
  hosts: z.array(
    z.object({
      trainerId: idSchema.nullable(),
      staffUserId: idSchema.nullable(),
      name: z.string(),
      booked: z.number().int(),
      completed: z.number().int(),
      noShow: z.number().int(),
      cancelled: z.number().int(),
    }),
  ),
  packagesSold: z.array(
    z.object({
      planId: idSchema,
      planName: z.string(),
      count: z.number().int(),
      amountMinor: z.number().int().nullable().describe('Paid for them; null without finance.read'),
    }),
  ),
  sessionsOutstanding: z.number().int().describe('Sessions members hold today in packages that have not expired'),
  activePackages: z.number().int(),
});
export type AppointmentReportDto = z.infer<typeof appointmentReportSchema>;
